import { Component } from '@theme/component';

/**
 * IT — the price range bar.
 *
 * The bar is rendered server-side by `snippets/price-filter.liquid` inside
 * Horizon's <price-facet-component>, so it comes back at the right positions
 * after every facet re-render. Being a custom element it re-initialises on
 * insertion, and its handlers are declarative `on:` attributes, so nothing
 * has to be delegated from `document` to survive the morph.
 *
 * No filtering logic lives here. The handles write into the min/max fields
 * Horizon already renders, then let the resulting `change` bubble to
 * `price-facet-component`, which reads those fields and refetches. The bar
 * is a second way to move existing values, not a second filter.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} lo - The low handle.
 * @property {HTMLInputElement} hi - The high handle.
 * @property {HTMLElement} fill - The filled span between the handles.
 * @extends Component<Refs>
 */
class ItPriceSlider extends Component {
  requiredRefs = ['lo', 'hi', 'fill'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    // Horizon's clear button blanks every input inside the facet, which for a
    // range input means "jump to the middle". The server render that follows
    // puts the handles back, but the flash in between reads as a bug — so the
    // extremes are restored on the next frame.
    this.closest('price-facet-component')?.addEventListener('click', this.#onFacetClick, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /** The facet's own min/max fields, which are the source of truth. */
  get #fields() {
    const facet = this.closest('price-facet-component');
    return {
      lo: facet?.querySelector('input[name$=".gte"]'),
      hi: facet?.querySelector('input[name$=".lte"]'),
    };
  }

  get #range() {
    const { lo, hi } = this.refs;
    const max = Number(lo.max) || 0;

    return {
      max,
      lo: Math.min(Number(lo.value), Number(hi.value)),
      hi: Math.max(Number(lo.value), Number(hi.value)),
    };
  }

  /**
   * A handle moved. Wired from the handles' `on:input="/move"`.
   * @param {InputEvent & { target: HTMLInputElement }} event
   */
  move(event) {
    const { lo, hi } = this.refs;

    // Keep the handles from crossing: each one stops at the other.
    if (event.target === lo && Number(lo.value) > Number(hi.value)) lo.value = hi.value;
    if (event.target === hi && Number(hi.value) < Number(lo.value)) hi.value = lo.value;

    this.#paint();

    // An untouched end means "no bound", which is an empty field — writing 0
    // or the ceiling there would add a filter the shopper never asked for.
    const range = this.#range;
    const { lo: loField, hi: hiField } = this.#fields;
    if (loField instanceof HTMLInputElement) loField.value = range.lo > 0 ? String(range.lo) : '';
    if (hiField instanceof HTMLInputElement) hiField.value = range.hi < range.max ? String(range.hi) : '';
  }

  /**
   * A field was typed into. Wired from the fields' `on:input="#<id>/fromField"`.
   *
   * The fields stay editable, so typing in one has to move its handle —
   * otherwise the bar and the boxes drift apart until the next server render.
   * Repaint only: writing the parsed number back would clobber the field the
   * shopper is still typing in.
   * @param {InputEvent & { target: HTMLInputElement }} event
   */
  fromField(event) {
    const field = event.target;
    const isMin = field.name.endsWith('.gte');
    const handle = isMin ? this.refs.lo : this.refs.hi;
    const digits = field.value.replace(/[^\d.]/g, '');

    handle.value = digits === '' ? (isMin ? handle.min : handle.max) : digits;
    this.#paint();
  }

  #paint() {
    const { max, lo, hi } = this.#range;
    if (max <= 0) return;

    this.style.setProperty('--it-lo', `${(lo / max) * 100}%`);
    this.style.setProperty('--it-hi', `${(hi / max) * 100}%`);
    this.refs.fill.classList.toggle('is-active', lo > 0 || hi < max);
  }

  /** @param {MouseEvent} event */
  #onFacetClick = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('facet-clear-component')) return;

    requestAnimationFrame(() => {
      const { lo, hi } = this.refs;
      lo.value = lo.min;
      hi.value = hi.max;
      this.#paint();
    });
  };
}

if (!customElements.get('it-price-slider')) {
  customElements.define('it-price-slider', ItPriceSlider);
}
